import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../context/LanguageContext';

const PayWaterTax = () => {
  const { language } = useLanguage();
  
  const [formData, setFormData] = useState({
    ownerName: '',
    waterConnectionNumber: '',
    mobileNumber: '',
    year: '',
    amount: '',
    paymentMethod: '',
    upiId: 'sangramshinde7007@okhdfcbank', // default UPI
    cardNumber: '',
    cardExpiry: '',
    cardCVV: '',
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-assign water connection number and tax amount based on owner name
    if (name === 'ownerName') {
      let waterConnectionNumber = '';
      let amount = '';
      
      if (value.toLowerCase() === 'shubham') {
        waterConnectionNumber = '1';
        amount = '10';
      } else if (value.toLowerCase() === 'sangram') {
        waterConnectionNumber = '2';
        amount = '20';
      } else if (value.toLowerCase() === 'sagar') {
        waterConnectionNumber = '3';
        amount = '30';
      }
      
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        waterConnectionNumber: waterConnectionNumber,
        amount: amount,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!formData.ownerName || !formData.waterConnectionNumber || !formData.mobileNumber || !formData.year) {
        alert(language === 'marathi' ? 'कृपया सर्व अनिवार्य माहिती प्रविष्ट करा' : 'Please enter all mandatory information');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePayment = (e) => {
    e.preventDefault();
    setIsPaymentProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setIsPaymentProcessing(false);
      
      // Create receipt data
      const receipt = {
        ownerName: formData.ownerName,
        waterConnectionNumber: formData.waterConnectionNumber,
        mobileNumber: formData.mobileNumber,
        year: formData.year,
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        transactionId: 'WTX' + Date.now(),
        paymentDate: new Date().toLocaleDateString(language === 'marathi' ? 'mr-IN' : 'en-IN'),
        paymentTime: new Date().toLocaleTimeString(language === 'marathi' ? 'mr-IN' : 'en-IN'),
        grampanchayat: language === 'marathi' ? 'इनाम धामणी ग्रामपंचायत' : 'Inam Dhamani Gram Panchayat'
      };
      
      setReceiptData(receipt);
      setPaymentSuccess(true);
      
      alert(language === 'marathi' 
        ? 'पाणीपट्टी पेमेंट यशस्वीरित्या पूर्ण झाले! तुमच्या मोबाईलवर पुष्टीकरण संदेश पाठवला जाईल.'
        : 'Water tax payment successful! A confirmation message will be sent to your mobile.');
    }, 2000);
  };

  const handleReset = () => {
    setFormData({
      ownerName: '',
      waterConnectionNumber: '',
      mobileNumber: '',
      year: '',
      amount: '',
      paymentMethod: '',
      upiId: 'sangramshinde7007@okhdfcbank',
      cardNumber: '',
      cardExpiry: '',
      cardCVV: '',
    });
    setCurrentStep(1);
  };

  const upiQRValue =
    formData.upiId && formData.amount
      ? `upi://pay?pa=${formData.upiId}&pn=Inam%20Dhamani%20Grampanchayat&am=${formData.amount}&cu=INR`
      : '';

  const handleDownloadReceipt = () => {
    if (!receiptData) return;
    
    // Create receipt content
    const receiptContent = `
╔══════════════════════════════════════════════════════════════╗
║                  ${language === 'marathi' ? 'पाणीपट्टी पेमेंट रसीद' : 'Water Tax Payment Receipt'}                      ║
║                  ${receiptData.grampanchayat}                    ║
╠══════════════════════════════════════════════════════════════╣
║                                                              ║
║  ${language === 'marathi' ? 'रसीद क्रमांक:' : 'Receipt No.:'} ${receiptData.transactionId}                      ║
║  ${language === 'marathi' ? 'दिनांक:' : 'Date:'} ${receiptData.paymentDate}                              ║
║  ${language === 'marathi' ? 'वेळ:' : 'Time:'} ${receiptData.paymentTime}                                ║
║                                                              ║
╠══════════════════════════════════════════════════════════════╣
║                        ${language === 'marathi' ? 'मालक माहिती' : 'Owner Information'}                           ║
╠══════════════════════════════════════════════════════════════╣
║                                                              ║
║  ${language === 'marathi' ? 'मालकाचे नाव:' : 'Owner Name:'} ${receiptData.ownerName.padEnd(45)} ║
║  ${language === 'marathi' ? 'पाणी कनेक्शन क्रमांक:' : 'Water Connection No.:'} ${receiptData.waterConnectionNumber.padEnd(33)} ║
║  ${language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'} ${receiptData.mobileNumber.padEnd(39)} ║
║  ${language === 'marathi' ? 'वर्ष:' : 'Year:'} ${receiptData.year.padEnd(47)} ║
║                                                              ║
╠══════════════════════════════════════════════════════════════╣
║                       ${language === 'marathi' ? 'पेमेंट माहिती' : 'Payment Information'}                          ║
╠══════════════════════════════════════════════════════════════╣
║                                                              ║
║  ${language === 'marathi' ? 'पाणीपट्टी रक्कम:' : 'Water Tax Amount:'} ₹${receiptData.amount.padEnd(40)} ║
║  ${language === 'marathi' ? 'पेमेंट पद्धत:' : 'Payment Method:'} ${receiptData.paymentMethod.toUpperCase().padEnd(41)} ║
║                                                              ║
╠══════════════════════════════════════════════════════════════╣
║                                                              ║
║    ${language === 'marathi' ? 'ही रसीद कंप्युटरद्वारे निर्मित आहे' : 'This receipt is computer generated'}                      ║
║    ${language === 'marathi' ? 'कृपया ती सुरक्षित ठेवा' : 'Please keep it safe'}                                ║
║                                                              ║
╚══════════════════════════════════════════════════════════════╝
    `;
    
    // Create and download PDF
    const blob = new Blob([receiptContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `WaterTax_Receipt_${receiptData.transactionId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  
  const handleNewPayment = () => {
    setPaymentSuccess(false);
    setReceiptData(null);
    setFormData({
      ownerName: '',
      waterConnectionNumber: '',
      mobileNumber: '',
      year: '',
      amount: '',
      paymentMethod: '',
      upiId: 'sangramshinde7007@okhdfcbank',
      cardNumber: '',
      cardExpiry: '',
      cardCVV: '',
    });
    setCurrentStep(1);
  };

  const formSectionStyle = {
    background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    border: '1px solid #00bcd4',
    boxShadow: '0 4px 15px rgba(0, 188, 212, 0.1)',
  };

  const inputStyle = {
    background: '#ffffff',
    border: '1px solid #00bcd4',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    width: '100%',
  };

  const labelStyle = {
    color: '#006064',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'block',
  };

  const sectionTitleStyle = {
    color: '#006064',
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '3px solid #00bcd4',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  };

  const requiredStar = {
    color: '#ef4444',
    marginLeft: '3px',
    fontWeight: 'bold',
  };

  const helpTextStyle = {
    color: '#546e7a',
    fontSize: '12px',
    fontStyle: 'italic',
    marginTop: '4px',
    display: 'block',
  };

  const stepIndicatorStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '30px',
  };

  const stepStyle = (step) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentStep >= step ? '#00bcd4' : '#d1d5db',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    margin: '0 15px',
    position: 'relative',
  });

  const stepLineStyle = {
    width: '60px',
    height: '3px',
    background: '#d1d5db',
  };

  const paymentMethodStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginTop: '20px',
  };

  const paymentOptionStyle = (selected) => ({
    padding: '15px',
    border: `2px solid ${selected ? '#00bcd4' : '#d1d5db'}`,
    borderRadius: '8px',
    background: selected ? '#e0f7fa' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-teal-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Form Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 mb-4">
            <span className="text-3xl text-white">💧</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {language === 'marathi' ? 'पाणीपट्टी ऑनलाइन भरणा' : 'Water Tax Online Payment'}
          </h1>
          <p className="text-gray-600">
            {language === 'marathi' ? 'इनाम धामणी ग्रामपंचायत - स्वच्छ पाणी योजना कर भरणा' : 'Inam Dhamani Gram Panchayat - Clean Water Scheme Tax Payment'}
          </p>
          <div className="mt-4 w-24 h-1 bg-gradient-to-r from-cyan-500 to-teal-500 mx-auto rounded-full"></div>
        </div>

        {/* Step Indicator */}
        <div style={stepIndicatorStyle}>
          <div style={stepStyle(1)}>1</div>
          <div style={stepLineStyle}></div>
          <div style={stepStyle(2)}>2</div>
          <div style={stepLineStyle}></div>
          <div style={stepStyle(3)}>3</div>
        </div>
        <div className="text-center mb-8">
          <div className="flex justify-between text-sm font-medium text-gray-600 max-w-xs mx-auto">
            <span className={currentStep >= 1 ? 'text-cyan-600 font-bold' : ''}>{language === 'marathi' ? 'माहिती' : 'Info'}</span>
            <span className={currentStep >= 2 ? 'text-cyan-600 font-bold' : ''}>{language === 'marathi' ? 'रक्कम' : 'Amount'}</span>
            <span className={currentStep >= 3 ? 'text-cyan-600 font-bold' : ''}>{language === 'marathi' ? 'पेमेंट' : 'Payment'}</span>
          </div>
        </div>

        <form onSubmit={handlePayment}>
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div style={formSectionStyle}>
              <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'पाणी कनेक्शन माहिती' : 'Water Connection Information'}</h2>
              
              <div className="space-y-6">
                {/* Owner Name */}
                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'मालकाचे नाव' : 'Owner Name'} <span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder={language === 'marathi' ? 'पाणी कनेक्शन मालकाचे संपूर्ण नाव' : 'Enter water connection owner\'s full name'}
                    list="defaultNames"
                  />
                  <datalist id="defaultNames">
                    <option value="shubham" />
                    <option value="sangram" />
                    <option value="sagar" />
                  </datalist>
                  <span style={helpTextStyle}>
                    {language === 'marathi' ? 'ज्या व्यक्तीच्या नावावर पाणी कनेक्शन आहे त्या व्यक्तीचे नाव' : 'Name of the person on whose name the water connection is registered'}
                  </span>
                </div>

                {/* Water Connection Number */}
                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'पाणी कनेक्शन क्रमांक' : 'Water Connection Number'} <span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    name="waterConnectionNumber"
                    value={formData.waterConnectionNumber}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder={language === 'marathi' ? 'पाणी कनेक्शन क्रमांक प्रविष्ट करा' : 'Enter water connection number'}
                    className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <span style={helpTextStyle}>
                    {language === 'marathi' ? 'ग्रामपंचायत कार्यालयातील पाणी कनेक्शन रेकॉर्ड क्रमांक' : 'Water connection record number in Gram Panchayat office'}
                  </span>
                </div>

                {/* Mobile Number */}
                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'मोबाईल व्हॉट्सॲप क्रमांक' : 'Mobile WhatsApp Number'} <span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder={language === 'marathi' ? 'संपर्कासाठी मोबाईल क्रमांक' : 'Mobile number for contact'}
                    pattern="[0-9]{10}"
                    maxLength="10"
                    className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <span style={helpTextStyle}>
                    {language === 'marathi' ? 'पेमेंट पुष्टीकरणासाठी 10 अंकी मोबाईल क्रमांक' : '10 digit mobile number for payment confirmation'}
                  </span>
                  {formData.mobileNumber && formData.mobileNumber.length !== 10 && (
                    <span style={{color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      {language === 'marathi' ? '⚠️ मोबाईल क्रमांक 10 अंकी असावा' : '⚠️ Mobile number should be 10 digits'}
                    </span>
                  )}
                </div>

                {/* Year */}
                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'वर्ष' : 'Year'} <span style={requiredStar}>*</span>
                  </label>
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="">{language === 'marathi' ? 'निवडा' : 'Select'}</option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2023-2024">2023-2024</option>
                    <option value="2022-2023">2022-2023</option>
                    <option value="2021-2022">2021-2022</option>
                    <option value="arrears">{language === 'marathi' ? 'मागील बाकी' : 'Previous Arrears'}</option>
                    <option value="2020-2021">2020-2021</option>
                    <option value="2019-2020">2019-2020</option>
                    <option value="2018-2019">2018-2019</option>
                  </select>
                  <span style={helpTextStyle}>
                    {language === 'marathi' ? 'ज्या वर्षासाठी पाणीपट्टी भरणा करायचा आहे ते वर्ष निवडा' : 'Select the year for which you want to pay the water tax'}
                  </span>
                </div>
              </div>

              {/* Step 1 Buttons */}
              <div className="flex justify-end mt-8">
                <button
                  type="button"
                  onClick={handleNextStep}
                  style={{
                    background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0, 188, 212, 0.3)',
                  }}
                  className="hover:shadow-lg transform hover:-translate-y-1"
                >
                  {language === 'marathi' ? 'पुढे जा →' : 'Proceed →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Amount Calculation */}
          {currentStep === 2 && (
            <div style={formSectionStyle}>
              <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'पाणीपट्टी रक्कम' : 'Water Tax Amount'}</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <h3 className="font-bold text-cyan-800 mb-2">{language === 'marathi' ? 'पाणी कनेक्शन तपशील:' : 'Water Connection Details:'}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मालकाचे नाव:' : 'Owner Name:'}</span>
                      <p className="font-semibold">{formData.ownerName || '---'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'कनेक्शन क्रमांक:' : 'Connection Number:'}</span>
                      <p className="font-semibold">{formData.waterConnectionNumber || '---'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'वर्ष:' : 'Year:'}</span>
                      <p className="font-semibold">{formData.year || '---'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'}</span>
                      <p className="font-semibold">{formData.mobileNumber || '---'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'पाणीपट्टी रक्कम (₹)' : 'Water Tax Amount (₹)'} <span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder={language === 'marathi' ? 'पाणीपट्टी रक्कम प्रविष्ट करा' : 'Enter water tax amount'}
                    min="0"
                    className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <span style={helpTextStyle}>
                    {language === 'marathi' ? 'ग्रामपंचायत कार्यालयातून मिळालेली पाणीपट्टी रक्कम प्रविष्ट करा' : 'Enter the water tax amount obtained from the Gram Panchayat office'}
                  </span>
                </div>

                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <h3 className="font-bold text-teal-800 mb-2">{language === 'marathi' ? 'पाणीपट्टी तपशील:' : 'Water Tax Details:'}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{language === 'marathi' ? 'मूळ पाणीपट्टी:' : 'Basic Water Tax:'}</span>
                      <span className="font-semibold">₹ {formData.amount || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{language === 'marathi' ? 'वापर शुल्क (जर लागू):' : 'Usage Fee (if applicable):'}</span>
                      <span className="font-semibold">₹ 0</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{language === 'marathi' ? 'दंड (जर लागू असेल):' : 'Penalty (if applicable):'}</span>
                      <span className="font-semibold text-red-600">₹ 0</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-bold">{language === 'marathi' ? 'एकूण रक्कम:' : 'Total Amount:'}</span>
                      <span className="font-bold text-lg text-green-700">
                        ₹ {formData.amount || '0'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-800 mb-2">{language === 'marathi' ? '💧 पाणी वापर माहिती:' : '💧 Water Usage Info:'}</h4>
                  <p className="text-sm text-blue-700">
                    {language === 'marathi' 
                      ? 'पाणीपट्टी रक्कम तुमच्या पाणी वापरावर आधारित असते. मासिक वापर नोंदणी तपासण्यासाठी ग्रामसेवक कार्यालयात संपर्क साधा.'
                      : 'The water tax amount is based on your water usage. Contact the Gram Sevak office to check the monthly usage registration.'}
                  </p>
                </div>
              </div>

              {/* Step 2 Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  style={{
                    background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  className="hover:shadow-lg"
                >
                  {language === 'marathi' ? '← मागे जा' : '← Go Back'}
                </button>
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!formData.amount}
                  style={{
                    background: !formData.amount 
                      ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
                      : 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: !formData.amount ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: !formData.amount ? 0.6 : 1,
                  }}
                  className="hover:shadow-lg transform hover:-translate-y-1"
                >
                  {language === 'marathi' ? 'पेमेंट करा →' : 'Pay →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment Method */}
          {currentStep === 3 && (
            <div style={formSectionStyle}>
              <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'पेमेंट पद्धत' : 'Payment Method'}</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-cyan-800">{language === 'marathi' ? 'एकूण पाणीपट्टी:' : 'Total Water Tax:'}</h3>
                      <p className="text-2xl font-bold text-green-700">₹ {formData.amount}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{language === 'marathi' ? 'कनेक्शन क्रमांक' : 'Connection Number'}</p>
                      <p className="font-semibold">{formData.waterConnectionNumber}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-700 mb-4">{language === 'marathi' ? 'पेमेंट पद्धत निवडा:' : 'Select Payment Method:'}</h3>
                  <div style={paymentMethodStyle}>
                    {/* UPI Option */}
                    <div 
                      style={paymentOptionStyle(formData.paymentMethod === 'upi')}
                      onClick={() => setFormData({...formData, paymentMethod: 'upi'})}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mr-4">
                          <span className="text-xl">💳</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{language === 'marathi' ? 'UPI पेमेंट' : 'UPI Payment'}</h4>
                          <p className="text-sm text-gray-600">Google Pay, PhonePe, Paytm</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'upi' && (
                        <div className="mt-5 space-y-5">
                          <div>
                            <label style={labelStyle}>
                              UPI ID <span style={requiredStar}>*</span>
                            </label>
                            <input
                              type="text"
                              name="upiId"
                              value={formData.upiId}
                              onChange={handleChange}
                              required
                              style={inputStyle}
                              placeholder={language === 'marathi' ? 'उदा: yourname@okhdfcbank' : 'e.g. yourname@okhdfcbank'}
                            />
                          </div>

                          {upiQRValue && (
                            <div className="flex flex-col items-center p-5 bg-white border rounded-xl shadow-sm">
                              <p className="font-semibold mb-3">{language === 'marathi' ? 'QR स्कॅन करा' : 'Scan QR'}</p>
                              <QRCodeSVG
                                value={upiQRValue}
                                size={220}
                                level="H"
                                fgColor="#000000"
                                bgColor="#ffffff"
                                marginSize={4}
                              />
                              <p className="mt-3 text-sm font-medium">
                                {language === 'marathi' ? 'रक्कम:' : 'Amount:'} ₹ {formData.amount}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formData.upiId}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Card Option */}
                    <div 
                      style={paymentOptionStyle(formData.paymentMethod === 'card')}
                      onClick={() => setFormData({...formData, paymentMethod: 'card'})}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mr-4">
                          <span className="text-xl">💳</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{language === 'marathi' ? 'कार्ड पेमेंट' : 'Card Payment'}</h4>
                          <p className="text-sm text-gray-600">{language === 'marathi' ? 'डेबिट/क्रेडिट कार्ड' : 'Debit/Credit Card'}</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'card' && (
                        <div className="mt-4 space-y-4">
                          <div>
                            <label style={labelStyle}>
                              {language === 'marathi' ? 'कार्ड नंबर' : 'Card Number'} <span style={requiredStar}>*</span>
                            </label>
                            <input
                              type="text"
                              name="cardNumber"
                              value={formData.cardNumber}
                              onChange={handleChange}
                              required
                              style={inputStyle}
                              placeholder="1234 5678 9012 3456"
                              maxLength="19"
                              className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label style={labelStyle}>
                                {language === 'marathi' ? 'मुदत' : 'Expiry'} <span style={requiredStar}>*</span>
                              </label>
                              <input
                                type="text"
                                name="cardExpiry"
                                value={formData.cardExpiry}
                                onChange={handleChange}
                                required
                                style={inputStyle}
                                placeholder="MM/YY"
                                maxLength="5"
                                className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                              />
                            </div>
                            <div>
                              <label style={labelStyle}>
                                CVV <span style={requiredStar}>*</span>
                              </label>
                              <input
                                type="password"
                                name="cardCVV"
                                value={formData.cardCVV}
                                onChange={handleChange}
                                required
                                style={inputStyle}
                                placeholder="123"
                                maxLength="3"
                                className="focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Net Banking */}
                    <div 
                      style={paymentOptionStyle(formData.paymentMethod === 'netbanking')}
                      onClick={() => setFormData({...formData, paymentMethod: 'netbanking'})}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mr-4">
                          <span className="text-xl">🏦</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{language === 'marathi' ? 'नेट बँकिंग' : 'Net Banking'}</h4>
                          <p className="text-sm text-gray-600">{language === 'marathi' ? 'इंटरनेट बँकिंग' : 'Internet Banking'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Security Info */}
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-start">
                    <span className="text-teal-600 mr-2">🔒</span>
                    <div>
                      <h4 className="font-bold text-teal-800">{language === 'marathi' ? 'सुरक्षित पेमेंट' : 'Secure Payment'}</h4>
                      <p className="text-sm text-teal-700">
                        {language === 'marathi' 
                          ? '256-bit SSL एन्क्रिप्शनद्वारे संरक्षित. तुमची पेमेंट माहिती सुरक्षित आहे.'
                          : 'Protected by 256-bit SSL encryption. Your payment information is secure.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  style={{
                    background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  className="hover:shadow-lg"
                >
                  {language === 'marathi' ? '← मागे जा' : '← Go Back'}
                </button>
                <button
                  type="submit"
                  disabled={!formData.paymentMethod || isPaymentProcessing}
                  style={{
                    background: !formData.paymentMethod || isPaymentProcessing
                      ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
                      : 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: !formData.paymentMethod || isPaymentProcessing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: !formData.paymentMethod || isPaymentProcessing ? 0.6 : 1,
                  }}
                  className="hover:shadow-lg transform hover:-translate-y-1"
                >
                  {isPaymentProcessing ? (
                    <>
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      {language === 'marathi' ? 'प्रक्रिया होत आहे...' : 'Processing...'}
                    </>
                  ) : (
                    language === 'marathi' ? '💧 पाणीपट्टी सबमिट करा' : '💧 Submit Water Tax'
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Payment Success Screen */}
        {paymentSuccess && receiptData && (
          <div style={formSectionStyle}>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-green-600 mb-4">
                <span className="text-4xl">✅</span>
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">
                {language === 'marathi' ? 'पाणीपट्टी पेमेंट यशस्वीरित्या पूर्ण झाले!' : 'Water Tax Payment Successful!'}
              </h2>
              <p className="text-gray-600">
                {language === 'marathi' ? 'तुमच्या मोबाईलवर पुष्टीकरण संदेश पाठवला जाईल' : 'A confirmation message will be sent to your mobile'}
              </p>
            </div>

            {/* Receipt Details */}
            <div className="bg-white border-2 border-green-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-center mb-4 text-cyan-800">
                {language === 'marathi' ? '💧 पाणीपट्टी पेमेंट रसीद' : '💧 Water Tax Payment Receipt'}
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'रसीद क्रमांक:' : 'Receipt No.:'}</span>
                  <span className="font-mono">{receiptData.transactionId}</span>
                </div>
                
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                  <span>{receiptData.paymentDate}</span>
                </div>
                
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'वेळ:' : 'Time:'}</span>
                  <span>{receiptData.paymentTime}</span>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-bold text-gray-700 mb-2">{language === 'marathi' ? 'मालक माहिती:' : 'Owner Information:'}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मालकाचे नाव:' : 'Owner Name:'}</span>
                      <p className="font-semibold">{receiptData.ownerName}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'पाणी कनेक्शन क्रमांक:' : 'Water Connection No.:'}</span>
                      <p className="font-semibold">{receiptData.waterConnectionNumber}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'}</span>
                      <p className="font-semibold">{receiptData.mobileNumber}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'वर्ष:' : 'Year:'}</span>
                      <p className="font-semibold">{receiptData.year}</p>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-bold text-gray-700 mb-2">{language === 'marathi' ? 'पेमेंट माहिती:' : 'Payment Information:'}</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{language === 'marathi' ? 'पाणीपट्टी रक्कम:' : 'Water Tax Amount:'}</span>
                      <span className="font-bold text-green-700 text-lg">₹{receiptData.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{language === 'marathi' ? 'पेमेंट पद्धत:' : 'Payment Method:'}</span>
                      <span className="font-semibold">{receiptData.paymentMethod.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-center text-xs text-gray-500 mt-4 pt-3 border-t">
                  <p>{language === 'marathi' ? 'ही रसीद कंप्युटरद्वारे निर्मित आहे' : 'This receipt is computer generated'}</p>
                  <p>{language === 'marathi' ? 'कृपया ती सुरक्षित ठेवा' : 'Please keep it safe'}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleDownloadReceipt}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>📄</span>
                {language === 'marathi' ? 'रसीड डाउनलोड करा' : 'Download Receipt'}
              </button>
              
              <button
                onClick={handleNewPayment}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>🔄</span>
                {language === 'marathi' ? 'नवीन पेमेंट करा' : 'Make New Payment'}
              </button>
            </div>
          </div>
        )}

        {/* Information Box */}
        <div className="mt-10 p-6 bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-lg">
          <h3 className="text-lg font-semibold text-cyan-800 mb-3">
            {language === 'marathi' ? '💧 पाणीपट्टी भरणा माहिती:' : '💧 Water Tax Payment Info:'}
          </h3>
          <ul className="list-disc pl-5 space-y-2 text-cyan-700">
            <li className="font-medium">
              {language === 'marathi' 
                ? 'ऑनलाइन पाणीपट्टी भरण्यासाठी कोणतेही अतिरिक्त शुल्क नाही' 
                : 'There are no additional charges for online water tax payment'}
            </li>
            <li className="font-medium">
              {language === 'marathi'
                ? 'पेमेंट पूर्ण झाल्यानंतर त्वरित पुष्टीकरण मिळेल'
                : 'Immediate confirmation will be received after payment is complete'}
            </li>
            <li className="font-medium">
              {language === 'marathi'
                ? 'पाणीपट्टी रसीद तुमच्या ईमेल व मोबाईलवर पाठवली जाईल'
                : 'Water tax receipt will be sent to your email and mobile'}
            </li>
            <li className="font-medium">
              {language === 'marathi'
                ? 'पाणी कनेक्शन क्रमांक तपासण्यासाठी ग्रामसेवक कार्यालयात संपर्क साधा'
                : 'Contact Gram Sevak office to check water connection number'}
            </li>
            <li className="font-medium">
              {language === 'marathi'
                ? 'नियमित पाणीपट्टी भरण्यासाठी पाणी सेवा सुरू राहील'
                : 'Water service will continue for regular water tax payment'}
            </li>
          </ul>
          
          <div className="mt-4 pt-4 border-t border-cyan-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-bold text-cyan-800 mb-2">
                  {language === 'marathi' ? '📞 संपर्क क्रमांक:' : '📞 Contact Number:'}
                </h4>
                <p className="text-cyan-700">
                  {language === 'marathi' ? '०२४१-XXXXXXX (पाणीपुरवठा विभाग)' : '0241-XXXXXXX (Water Supply Dept)'}
                </p>
              </div>
              <div>
                <h4 className="font-bold text-cyan-800 mb-2">✉️ ईमेल:</h4>
                <p className="text-cyan-700">watertax@inamdhamani-grampanchayat.in</p>
              </div>
            </div>
          </div>
        </div>

        {/* Water Conservation Message */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <span className="text-2xl mr-3">💦</span>
            <div>
              <h4 className="font-bold text-blue-800">
                {language === 'marathi' ? 'पाणी वाचवा, जीवन वाचवा!' : 'Save Water, Save Life!'}
              </h4>
              <p className="text-sm text-blue-700">
                {language === 'marathi'
                  ? 'नियमित पाणीपट्टी भरून स्वच्छ पाणी योजनेला सहकार्य करा. अनावश्यक पाणी वापर टाळा व जलसंरक्षणासाठी सहभागी व्हा.'
                  : 'Cooperate with the clean water scheme by paying water tax regularly. Avoid unnecessary water usage and participate in water conservation.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayWaterTax;